import { format, addYears } from 'date-fns';
import { th as thLocale } from 'date-fns/locale';
import { toZonedTime } from 'date-fns-tz';
import { BANK_OPTIONS, type BankOption } from '@/modules/shared/constants';

const THAI_TZ = 'Asia/Bangkok';

/**
 * Set or remove a value in localStorage.
 * Pass null or undefined to remove the item.
 */
export const setLocalStorageItem = (key: string, value: string | number | null | undefined): void => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(key, String(value));
};

/**
 * Get a value from localStorage.
 * Returns null if the item doesn't exist.
 */
export const getLocalStorageItem = (key: string): string | null => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(key);
};

/**
 * Remove a value from localStorage.
 */
export const removeLocalStorageItem = (key: string): void => {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(key);
};

/**
 * Get auth tokens from localStorage
 */
/**
 * Read a stored auth token, treating the literal string `"undefined"` as absent.
 *
 * `localStorage.setItem` coerces its value, so `setItem('accessToken', undefined)` stores the
 * nine characters `undefined`. That is reachable — `setAccessToken(data.access_token)` has no
 * guard, so a response missing the field writes it, as do the cookie-scavenging paths in
 * `utils/helper`. Read back unguarded the result is a truthy string, so route guards treat it as
 * a live session and requests go out as `Authorization: Bearer undefined` or `?token=undefined`.
 *
 * `utils/helper`'s `getLocalData` does not rescue it either: it runs `JSON.parse`, which throws
 * on `undefined`, and the catch hands back the raw string.
 *
 * The ops dashboard has carried this guard all along; this is the portal adopting it.
 */
export const getStoredToken = (key: string): string | null => {
  const raw = getLocalStorageItem(key);
  return raw && raw !== 'undefined' ? raw : null;
};

export const getAuthTokens = (): { token: string | null; refreshToken: string | null } => ({
  token: getLocalStorageItem('accessToken'),
  refreshToken: getLocalStorageItem('refreshToken'),
});
/**
 * Opens an external URL. Defaults to a new tab; pass `{ sameTab: true }` to navigate in the
 * current tab instead.
 * @param url - The URL to open
 * @param params - Optional URL parameters as an object
 * @param options - `{ sameTab }` — when true, navigate in the current tab (default: new tab);
 *   `{ fallbackToSameTab }` — when true and the new-tab popup is blocked, navigate in the
 *   current tab instead of silently doing nothing (FTT-16106)
 */
export const openExternalUrl = (
  url: string,
  params?: Record<string, string | null>,
  options?: { sameTab?: boolean; fallbackToSameTab?: boolean },
): void => {
  const navigateTo = (fullUrl: string) => {
    if (options?.sameTab) {
      window.location.assign(fullUrl);
      return;
    }
    if (options?.fallbackToSameTab) {
      // Passing `noopener` in the features string makes window.open return null
      // even on success, so open without it and sever the opener manually — a
      // null return then reliably means the popup was blocked.
      const win = window.open(fullUrl, '_blank');
      if (win) {
        win.opener = null;
      } else {
        window.location.assign(fullUrl);
      }
      return;
    }
    window.open(fullUrl, '_blank', 'noopener,noreferrer');
  };

  if (!params) {
    navigateTo(url);
    return;
  }

  const queryString = Object.entries(params)
    .filter(([_, value]) => value != null) // Filters out null and undefined
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value as string)}`)
    .join('&');

  const fullUrl = queryString ? `${url}${url.includes('?') ? '&' : '?'}${queryString}` : url;
  navigateTo(fullUrl);
};

/**
 * Open the OPS Dashboard for a quotation.
 *
 * The dashboard is an in-app route (`/ops-dashboard`, see modules/ops-dashboard/OpsDashboard.tsx),
 * so this is just a navigation. It used to branch between an iframe of a separately-deployed
 * origin and a redirect carrying `?token=&refresh_token=`; neither exists now — same origin means
 * the dashboard reads the tokens the portal already holds in localStorage.
 *
 * Navigates in the current tab by default; pass `{ sameTab: false }` to open a new tab instead.
 * If a new-tab popup is blocked (e.g. the open fires after an async API call, outside the
 * user-gesture window), falls back to navigating in the current tab (FTT-16106).
 */
export const openOpsDashboardWithAuth = (
  params?: Record<string, string | null | undefined>,
  options?: { sameTab?: boolean },
): void => {
  // Always the in-process route on this branch: `/ops-dashboard` is a portal route, so there is no
  // second origin and no `?token=` redirect. Upstream still branches on
  // `USE_EMBEDDED_OPS_DASHBOARD` and otherwise sends tokens to `OPS_DASHBOARD_URL`; CP7/CP8 deleted
  // that path here, so only the embedded arm survives.
  //
  // Routed through `openExternalUrl` purely for its popup-blocked fallback (FTT-16106).
  //
  // The same-tab default is upstream's (`4500c5bc7`) and is taken deliberately: only five call
  // sites ever passed `sameTab` explicitly, so the old new-tab default was not what most of them
  // wanted. Spreading `options` instead would silently keep the old behaviour, since an absent
  // `sameTab` is falsy.
  const resolvedOptions = { sameTab: options?.sameTab ?? true, fallbackToSameTab: true };

  const queryString = Object.entries(params ?? {})
    .filter(([, value]) => value != null)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value as string)}`)
    .join('&');
  const url = queryString ? `/ops-dashboard?${queryString}` : '/ops-dashboard';

  openExternalUrl(url, undefined, resolvedOptions);
};

/**
 * Absolute OPS Dashboard URL for the given params — the same destination
 * `openOpsDashboardWithAuth` navigates to, but returned instead of opened, and
 * deliberately WITHOUT auth tokens so it is safe to put on a clipboard or in a
 * message. Whoever opens it authenticates with their own session.
 *
 * Always the same-origin `/ops-dashboard` route, absolute so the link survives being pasted
 * elsewhere. Upstream picks between that and a separately-deployed `OPS_DASHBOARD_URL` via
 * `USE_EMBEDDED_OPS_DASHBOARD`; on this branch the dashboard is a portal route, so there is no
 * second origin to choose.
 */
export const buildOpsDashboardUrl = (params?: Record<string, string | null | undefined>): string => {
  const queryString = Object.entries(params ?? {})
    .filter(([, value]) => value != null)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value as string)}`)
    .join('&');
  const base = `${window.location.origin}/ops-dashboard`;
  return queryString ? `${base}?${queryString}` : base;
};

/**
 * First usable affiliate id from the candidates, highest priority first.
 *
 * Most routes carry no `?affiliate=`, so callers must fall back to the quotation's own
 * affiliate. Without it requests use the axios `VOU-AFF-ID` default — the logged-in
 * user — and an admin gets admin-scoped data back. Returns 0 when nothing resolves, which `affiliateRequestConfig` sends as
 * no header rather than a bogus id.
 */
export const resolveFlowAffiliateId = (...candidates: Array<number | string | null | undefined>): number => {
  for (const candidate of candidates) {
    const id = typeof candidate === 'number' ? candidate : parseInt(String(candidate ?? ''), 10);
    if (Number.isFinite(id) && id > 0) return id;
  }
  return 0;
};

/**
 * Affiliate request config
 * @param affiliateId - Affiliate ID
 * @returns Affiliate request config
 */
export const affiliateRequestConfig = (affiliateId: number | string): { headers?: { 'VOU-AFF-ID': string } } => {
  let affiliate = Number(affiliateId);
  const config: { headers?: { 'VOU-AFF-ID': string } } = {};
  if (affiliate && !isNaN(affiliate)) {
    config.headers = { 'VOU-AFF-ID': affiliate.toString() };
  }
  return config;
};

/** Tri-state boolean → label; `null`/`undefined` is the "not asked" dash. */
export const triStateLabel = (value: boolean | null | undefined, yes: string, no: string, na = '—'): string =>
  value == null ? na : value ? yes : no;

/**
 * Format date to YYYY-MM-DD for API calls (some endpoints need this format)
 */
export const formatDateToYYYYMMDD = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * A stored date string → a `Date` for the date pickers, or null.
 *
 * The pickers take a `Date`; forms store the `yyyy-MM-dd` string the API wants, so every caller
 * would otherwise write `value ? new Date(value) : null`. That turns an unparseable value into an
 * Invalid Date, which a picker renders as blank — indistinguishable from "not set", and it stays
 * blank after the user opens the calendar. Returning null makes the two cases the same on purpose:
 * unset.
 */
export const parseDateOnly = (value?: string | null): Date | null => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * A `Date` → `yyyy-MM-ddTHH:mm`, read with the *local* getters.
 *
 * The inverse of `new Date('yyyy-MM-ddTHH:mm')`, which parses in the browser's zone. Used with
 * `parseDateOnly` to round-trip a **wall clock** through a date-time picker: both directions use
 * local getters, so the Date is only ever a carrier for the digits and is never read as an instant.
 * Do not use it to serialise a moment — see `bangkokDateTimeLocalToIso` for that.
 */
export const toDateTimeLocalString = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/**
 * Format date to ISO 8601 format for API calls
 */
export const formatDateToISO = (date: Date): string => {
  return date.toISOString();
};

export const getTodayDate = (): string => {
  return formatDateToISO(new Date());
};

export const getTodayDateYYYYMMDD = (): string => {
  return formatDateToYYYYMMDD(new Date());
};

/**
 * Return end of day (23:59:59.000) for a date string as ISO string.
 */
export const getEndOfDayISO = (dateStr: string): string => {
  const d = new Date(dateStr);
  d.setHours(23, 59, 59, 0);
  return d.toISOString();
};

/**
 * End of day (23:59:59) in Bangkok time for a date string, e.g. '2026-07-20T23:59:59+07:00'.
 * Use for API date-only fields: a UTC end-of-day renders as the next day in Bangkok.
 */
export const getEndOfDayBangkokISO = (date: string | null | undefined): string | undefined => {
  if (!date) return undefined;
  const dateObj = new Date(date);
  if (Number.isNaN(dateObj.getTime())) return undefined;
  return `${formatDateToYYYYMMDD(dateObj)}T23:59:59+07:00`;
};

/**
 * Start of day (00:00:00) in Bangkok time for a date string, e.g. '2026-07-20T00:00:00+07:00'.
 * Mirrors getEndOfDayBangkokISO — normalises a filter boundary to the Bangkok day edge
 * regardless of any time carried in the source instant.
 */
export const getStartOfDayBangkokISO = (date: string | null | undefined): string | undefined => {
  if (!date) return undefined;
  const dateObj = new Date(date);
  if (Number.isNaN(dateObj.getTime())) return undefined;
  return `${formatDateToYYYYMMDD(dateObj)}T00:00:00+07:00`;
};

/**
 * Today at end of day (23:59:59) as ISO string. Use for default end-date filters.
 */
export const getTodayEndOfDayISO = (): string => {
  return getEndOfDayISO(getTodayDateYYYYMMDD());
};

export const getDateDaysAfter = (daysAgo: number = 0): string => {
  const date = new Date();
  date.setDate(date.getDate() + daysAgo);
  return formatDateToISO(date);
};

export const getDateMonthsAfter = (months: number = 0): string => {
  const date = new Date();
  date.setMonth(date.getMonth() + months);
  return formatDateToISO(date);
};

export const getDateMonthsAfterYYYYMMDD = (months: number = 0): string => {
  const date = new Date();
  date.setMonth(date.getMonth() + months);
  return formatDateToYYYYMMDD(date);
};

/**
 * Parse a date string. **Date-only strings are parsed as LOCAL midnight, not UTC.**
 *
 * That distinction is the whole point of the `YYYY-MM-DD` branch below. `new Date('2026-01-01')` is
 * UTC midnight, and `formatDateToYYYYMMDD` reads local getters — so west of UTC the pair round-trips
 * to the *previous day* (`2026-01-01` → `2025-12-31`). Thailand is UTC+7, which is why this went
 * unnoticed. Fixed at CP24; `DD/MM/YYYY` was already local, and `T`/`Z` strings carry their own
 * offset and are left to the `Date` constructor.
 */
export const parseDate = (dateString: string): Date | null => {
  if (!dateString) return null;

  if (dateString.includes('T') || dateString.includes('Z')) {
    return new Date(dateString);
  }

  const parts = dateString.split('/');
  if (parts.length === 3) {
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);
    return new Date(year, month, day);
  }

  // `YYYY-MM-DD` — build from parts so it lands on local midnight.
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString);
  if (iso) {
    return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  }

  return new Date(dateString);
};

/**
 * Format a date string to YYYY-MM-DD. Empty or whitespace input returns ''; invalid input returns the original string.
 */
export const formatDateStringToYYYYMMDD = (dateStr: string | null | undefined): string => {
  if (!dateStr?.trim()) return '';
  const d = parseDate(dateStr.trim());
  return d && !Number.isNaN(d.getTime()) ? formatDateToYYYYMMDD(d) : dateStr;
};

/** Day + short month + year by default; pass `options` for a narrower label (e.g. `4 Aug`). */
export const formatDate = (
  dateString: string | undefined,
  locale: string = 'th-TH',
  options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' },
): string => {
  if (!dateString) return '-';
  try {
    const date = parseDate(dateString);
    if (!date || isNaN(date.getTime())) return dateString;

    return date.toLocaleDateString(locale, options);
  } catch {
    return dateString;
  }
};

export const formatDateTime = (dateString: string | undefined, locale: string = 'th-TH'): string => {
  if (!dateString) return '-';
  try {
    const date = parseDate(dateString);
    if (!date || isNaN(date.getTime())) return dateString;

    return date.toLocaleString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
};

/**
 * Convert date to Thai date time format (adds 543 years and formats as 'MMM d, yyyy h:mm a')
 */
export const toThaiDateTime = (date: string | Date | undefined): string => {
  if (!date) return '-';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(dateObj.getTime())) return '-';
  const thaiYear = addYears(dateObj, 543);
  return format(thaiYear, 'MMM d, yyyy h:mm a');
};

/**
 * Format date to YYYY-MM-DD HH:mm format with Thai timezone adjustment
 * @param dateString - Date string to format (can be ISO with Z or local time)
 * @returns Formatted string as YYYY-MM-DD HH:mm in Thai timezone
 */
export const formatDateTimeYYYYMMDD = (dateString: string | undefined): string => {
  if (!dateString) return '-';

  try {
    const date = parseDate(dateString);
    if (!date || isNaN(date.getTime())) return dateString;

    let displayDate: Date;

    // If UTC date (has Z suffix), we need to handle it specially
    if (dateString.includes('Z')) {
      // Get UTC time and add 7 hours for Thai timezone
      const utcTime = date.getTime();
      // Add 7 hours in milliseconds (7 * 60 * 60 * 1000)
      const thaiTime = utcTime + 7 * 60 * 60 * 1000;
      displayDate = new Date(thaiTime);

      // Now extract UTC components (since we've already adjusted for timezone)
      const year = displayDate.getUTCFullYear();
      const month = String(displayDate.getUTCMonth() + 1).padStart(2, '0');
      const day = String(displayDate.getUTCDate()).padStart(2, '0');
      const hours = String(displayDate.getUTCHours()).padStart(2, '0');
      const minutes = String(displayDate.getUTCMinutes()).padStart(2, '0');

      return `${year}-${month}-${day} ${hours}:${minutes}`;
    } else {
      // For non-UTC dates, use local time as-is
      displayDate = date;

      const year = displayDate.getFullYear();
      const month = String(displayDate.getMonth() + 1).padStart(2, '0');
      const day = String(displayDate.getDate()).padStart(2, '0');
      const hours = String(displayDate.getHours()).padStart(2, '0');
      const minutes = String(displayDate.getMinutes()).padStart(2, '0');

      return `${year}-${month}-${day} ${hours}:${minutes}`;
    }
  } catch {
    return dateString;
  }
};

/**
 * Format date to Thai Buddhist calendar date: DD MMM YYYY (e.g., "08 Jan 2569")
 * Always in Asia/Bangkok timezone. Adds 543 years.
 */
export const formatThaiDate = (dateString: string, locale?: string): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const zoned = addYears(toZonedTime(date, THAI_TZ), 543);
  return format(zoned, 'dd MMM yyyy', locale === 'th' ? { locale: thLocale } : undefined);
};

/**
 * Format time to HH:mm:ss in Asia/Bangkok timezone.
 */
export const formatThaiTime = (dateString: string): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  return format(toZonedTime(date, THAI_TZ), 'HH:mm:ss');
};

// ---------------------------------------------------------------------------
// Buddhist-era display formatters (DD/MM/BBBB)
//
// Moved here verbatim from `ops-dashboard/utils/dateHelpers.ts`, which existed only to hold them.
// They are NOT interchangeable with `formatThaiDate`/`toThaiDateTime` above, which render the
// same instant as `08 Jan 2569` and `Jan 8, 2569 2:30 PM`. These render `08/01/2569` and
// `08/01/2569 14:30:00`, and they return an em dash for a missing value where the date-fns
// helpers return a hyphen. Both styles are in use; picking the wrong one silently changes how
// dates read on screen.
//
// The implementation stays on `Intl` rather than being rewritten in date-fns: the point of the
// move is a single home, not a re-derivation of formatting that is already correct.
// ---------------------------------------------------------------------------

const BUDDHIST_ERA_OFFSET = 543;

/** Parse ISO timestamps and plain `YYYY-MM-DD` API date strings for display. */
function parseDisplayDate(raw: string | number | Date | null | undefined): Date | null {
  if (raw == null || raw === '') return null;
  if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : raw;
  if (typeof raw === 'number') {
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const s = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const date = new Date(s);
  return Number.isNaN(date.getTime()) ? null : date;
}

function bangkokParts(date: Date) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: THAI_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(date);
  const pick = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? '';
  return {
    day: pick('day'),
    month: pick('month'),
    year: Number(pick('year')),
    hour: pick('hour'),
    minute: pick('minute'),
    second: pick('second'),
  };
}

/**
 * Bangkok wall-clock helpers for `<input type="datetime-local">`.
 *
 * Bangkok is a fixed UTC+7 with no DST, which is what makes an offset constant sound here.
 *
 * These live beside `bangkokParts` rather than in a date module of their own: this file is already
 * the one place the app converts between stored instants and Thai wall clock, and a fourth date
 * helper is how the last three came to disagree. Ported from ops-dashboard-th `eab78ed` + `1d1bbbf`
 * as one end state — the first standardised `paid_at` on Bangkok time, the second corrected it to
 * parse the API's value as UTC when hydrating.
 */
const BANGKOK_OFFSET_MS = 7 * 60 * 60 * 1000;

function formatBangkokDateTimeLocal(date: Date): string {
  const { day, month, year, hour, minute } = bangkokParts(date);
  return `${year}-${month}-${day}T${hour}:${minute}`;
}

function hasExplicitTimeZone(value: string): boolean {
  return /[Zz]$|[+-]\d{2}:?\d{2}$/.test(value);
}

/**
 * Stored/API instants carry seconds or an explicit zone; a value with neither is an operator's or
 * OCR's wall clock, and must not be shifted.
 */
function isUtcApiTimestamp(value: string): boolean {
  return hasExplicitTimeZone(value) || /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}/.test(value);
}

/** Parse a backend timestamp as UTC even when the serializer drops the trailing `Z`. */
function parseUtcApiInstant(value: string): Date | null {
  const normalized = value.trim().replace(' ', 'T');
  if (hasExplicitTimeZone(normalized)) {
    const date = new Date(normalized);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const base = normalized.replace(/\.\d+$/, '');
  const date = new Date(`${base}Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Any stored instant or typed value → `YYYY-MM-DDTHH:mm` in **Bangkok** wall clock, whatever the
 * operator's browser timezone is.
 *
 * The shapes it has to tell apart, in order: a slip/OCR `DD/MM/YYYY` (Buddhist years accepted), a
 * value already in `datetime-local` shape, a UTC API instant, and a seconds-less ISO string, which
 * is wall clock rather than UTC.
 */
export function toBangkokDateTimeLocal(raw: string | number | Date | null | undefined): string {
  if (raw == null || raw === '') return '';
  if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? '' : formatBangkokDateTimeLocal(raw);
  if (typeof raw === 'number') {
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? '' : formatBangkokDateTimeLocal(date);
  }

  const value = String(raw).trim();
  const pad = (n: number) => String(n).padStart(2, '0');
  const toCe = (year: number) => (year > 2400 ? year - 543 : year);

  const dmy = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[ T](\d{1,2}):(\d{2}))?/);
  if (dmy) {
    const [, d, mo, y, h = '0', mi = '0'] = dmy;
    return `${toCe(Number(y))}-${pad(Number(mo))}-${pad(Number(d))}T${pad(Number(h))}:${pad(Number(mi))}`;
  }

  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return value;

  if (isUtcApiTimestamp(value)) {
    const date = parseUtcApiInstant(value);
    return date ? formatBangkokDateTimeLocal(date) : '';
  }

  const isoNoSeconds = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{1,2}):(\d{2}))?$/);
  if (isoNoSeconds) {
    const [, y, mo, d, h = '0', mi = '0'] = isoNoSeconds;
    return `${y}-${mo}-${d}T${pad(Number(h))}:${pad(Number(mi))}`;
  }

  const date = parseDisplayDate(value);
  return date ? formatBangkokDateTimeLocal(date) : '';
}

/** Current Bangkok wall clock as `YYYY-MM-DDTHH:mm`. */
export function nowBangkokDateTimeLocal(): string {
  return toBangkokDateTimeLocal(new Date());
}

/**
 * `YYYY-MM-DDTHH:mm` read as **Bangkok** wall clock → a UTC ISO string for the API.
 *
 * Subtracts the fixed +7, so 19:00 typed in Bangkok posts as `…T12:00:00.000Z`. Using
 * `new Date(local).toISOString()` instead reads the string in the browser's zone, which is the bug
 * this replaces: the same click saved a different instant depending on where the operator sat.
 */
export function bangkokDateTimeLocalToIso(local: string): string {
  const value = String(local ?? '').trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!m) return '';
  const [y, mo, d, h, mi] = m.slice(1, 6).map(Number);
  if ([y, mo, d, h, mi].some((n) => Number.isNaN(n))) return '';
  return new Date(Date.UTC(y, mo - 1, d, h, mi, 0, 0) - BANGKOK_OFFSET_MS).toISOString();
}

/** Date-only display — DD/MM/BBBB (Buddhist era). */
export function formatBuddhistDate(raw: string | number | Date | null | undefined): string {
  if (raw == null || raw === '') return '—';
  const s = String(raw).trim();
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (ymd) {
    return `${ymd[3]}/${ymd[2]}/${Number(ymd[1]) + BUDDHIST_ERA_OFFSET}`;
  }
  const date = parseDisplayDate(raw);
  if (!date) return '—';
  const { day, month, year } = bangkokParts(date);
  return `${day}/${month}/${year + BUDDHIST_ERA_OFFSET}`;
}

/** Date + time display — DD/MM/BBBB HH:mm:ss (Bangkok wall clock). */
export function formatBuddhistDateTime(raw: string | number | Date | null | undefined): string {
  if (raw == null || raw === '') return '—';
  const date = parseDisplayDate(raw);
  if (!date) return '—';
  const { day, month, year, hour, minute, second } = bangkokParts(date);
  return `${day}/${month}/${year + BUDDHIST_ERA_OFFSET} ${hour}:${minute}:${second}`;
}

/** Time-only display — HH:mm:ss (Bangkok wall clock). */
export function formatBuddhistTime(raw: string | number | Date | null | undefined): string {
  if (raw == null || raw === '') return '—';
  const date = parseDisplayDate(raw);
  if (!date) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: THAI_TZ,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(date);
}

// Helper function for comma-separated number formatting
function commaNumber(inputNumber: string | number, optionalSeparator?: string, optionalDecimalChar?: string) {
  // default `decimalChar` is a period
  const decimalChar = optionalDecimalChar || '.';
  let stringNumber: string; // we assign this in the switch block and need it later.
  {
    let number: number; // we assign this in the switch block and need it right after.
    switch (typeof inputNumber) {
      case 'string':
        // if there aren't enough digits to need separators then return it
        // NOTE: some numbers which are too small will get passed this
        //       when they have decimal values which make them too long here.
        //       but, the number value check after this switch will catch it.
        if (inputNumber.length < (inputNumber[0] === '-' ? 5 : 4)) {
          return inputNumber;
        }
        // remember it as a string in `stringNumber` and convert to a Number
        stringNumber = inputNumber;
        // if they're not using the Node standard decimal char then replace it
        // before converting.
        number = Number(decimalChar !== '.' ? stringNumber.replace(decimalChar, '.') : stringNumber);
        break;
      // convert to a string.
      // NOTE: don't check if the number is too small before converting
      //       because we'll need to return `stringNumber` anyway.

      case 'number':
        stringNumber = String(inputNumber);
        number = inputNumber;
        // create the string version with the decimalChar they specified.
        // this matches what the above case 'string' produces,
        // and, fixes the bug *not* doing this caused.
        if ('.' !== decimalChar && !Number.isInteger(inputNumber)) {
          stringNumber = stringNumber.replace('.', decimalChar);
        }
        break;

      // return invalid type as-is
      default:
        return inputNumber;
    }

    // when it doesn't need a separator or isn't a number then return it
    if ((-1000 < number && number < 1000) || isNaN(number) || !isFinite(number)) {
      return stringNumber;
    }
  }

  {
    // strip off decimal value to add back in later
    const decimalIndex = stringNumber.lastIndexOf(decimalChar);
    let decimal: string | undefined;
    if (decimalIndex > -1) {
      decimal = stringNumber.slice(decimalIndex);
      stringNumber = stringNumber.slice(0, decimalIndex);
    }

    // finally, parse the string. Note, default 'separator' is a comma.
    const parts = parse(stringNumber, optionalSeparator || ',');

    // if there's a decimal value then add it to the parts.
    if (decimal) {
      // NOTE: we sliced() it off including the decimalChar
      parts.push(decimal);
    }

    // combine all parts for the final string (note, has separators).
    return parts.join('');
  }
}

function parse(string: string, separator: string): string[] {
  // find first index to split the string at (where 1st separator goes).
  let i = ((string.length - 1) % 3) + 1;
  // above calculation is wrong when num is negative and a certain size.
  if (i === 1 && string[0] === '-') {
    i = 4; // example: -123,456,789  start at 4, not 1.
  }
  const strings = [
    // holds the string parts
    string.slice(0, i), // grab part before the first separator
  ];
  // split remaining string in groups of 3 where a separator belongs
  for (; i < string.length; i += 3) {
    strings.push(separator, string.substring(i, i + 3));
  }
  return strings;
}

export const commaSeperatedNumbers = (
  num: string | number | null | undefined,
  decimalPlaces: number = 2,
): string | number | null => {
  if (num === '-') {
    return num;
  }
  if (typeof num === 'string' && (num.indexOf('-') > -1 || num.indexOf(',') > -1)) {
    return num;
  }
  if (isNaN(Number(num))) {
    return 0;
  }
  if (!num && num !== '0' && num !== 0) {
    return null;
  }
  return commaNumber(parseFloat(num.toString()).toFixed(decimalPlaces));
};

/**
 * Format number as Thai currency with ฿ symbol.
 *
 * A space follows the symbol (FTT-16140): `฿ 1,234.56`, not `฿1,234.56`. Displayed values only —
 * form inputs keep whatever the field does.
 *
 * A value that was never filled in (`null`, `undefined`, blank string) returns -
 * rather than `฿ 0`, so callers can tell it apart from a deliberate zero. A real `0` still
 * formats as `฿ 0`.
 *
 * @param num - Number or string to format (will be converted to number)
 * @param decimalPlaces - Number of decimal places (default: 2)
 * @returns Formatted string with ฿ symbol (e.g., "฿ 1,234.56"), or "–" when unfilled
 */
export const formatThaiCurrency = (num: string | number | null | undefined, decimalPlaces: number = 2): string => {
  if (num === null || num === undefined || (typeof num === 'string' && num.trim() === '')) {
    return '–';
  }

  // Handle string conversion - convert to number if it's a string representation of a number
  let numValue = num;
  if (typeof num === 'string') {
    // Handle empty string or '0' case
    if (num === '' || num === '0') {
      numValue = 0;
    } else {
      // Try to parse as float, fallback to 0 if invalid
      const parsed = parseFloat(num);
      numValue = isNaN(parsed) ? 0 : parsed;
    }
  }

  const formatted = commaSeperatedNumbers(numValue, decimalPlaces);
  if (formatted === null) return '฿ 0';
  return `฿ ${formatted}`;
};

/**
 * Stamp duty, always a whole baht (FTT-16090).
 *
 * Duty is 0.4% of the gross premium rounded UP, and the backend already stores it that way —
 * 13,869.75 x 0.004 = 55.479 comes back as 56, and 600 x 0.004 = 2.4 comes back as 3. Formatting
 * the raw figure with decimals showed values the backend never holds, and rounding rather than
 * ceiling would disagree with it in the other direction. Ceil keeps the two in step.
 *
 * @param withSymbol prefix the baht sign. Off for callers that render their own.
 */
export const formatDuty = (num: string | number | null | undefined, withSymbol: boolean = true): string => {
  const parsed = typeof num === 'string' ? parseFloat(num) : num;
  const value = parsed == null || isNaN(Number(parsed)) ? 0 : Math.ceil(Number(parsed));
  const formatted = commaSeperatedNumbers(value, 0) ?? 0;
  return withSymbol ? `฿ ${formatted}` : String(formatted);
};

/**
 * Convert snake_case to Title Case
 */
export const snakeToTitleCase = (str: string | number | undefined | null): string => {
  if (!str) return '-';
  // Coerce before splitting: callers pass API values typed `any`, and a numeric one used to throw
  // `str.split is not a function`. The ops-dashboard copy this replaces guarded it; the portal's
  // did not, so this is the guard moving over rather than a change for existing string callers.
  return String(str)
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const getGarageTypeLabel = (value: string | null | undefined): string => {
  if (value === '1') return 'Dealership';
  if (value === '0') return 'Garage';
  return 'Not specified';
};

/**
 * Validate email address format
 * @param email - Email address to validate
 * @returns true if email is valid, false otherwise
 */
export const isValidEmail = (email: string): boolean => {
  if (!email || typeof email !== 'string') return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

/**
 * Validates a Thai mobile number.
 * Accepts a 9-digit number (e.g. "812345678") or a 10-digit number with a leading zero ("0812345678").
 */
export const isValidMobileNumber = (value: string | null | undefined): boolean => {
  if (!value || !/^\d+$/.test(value)) return false;
  return value.replace(/^0+/, '').length === 9;
};

/** Convert a stored CE `yyyy-MM-dd` date string to Buddhist Era for display. */
export const ceToBeDate = (dateStr?: string): string => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts[0]) {
    const ce = parseInt(parts[0], 10);
    if (!Number.isNaN(ce)) {
      parts[0] = String(ce + 543);
      return parts.join('-');
    }
  }
  return dateStr;
};

/** Convert a Buddhist Era `yyyy-MM-dd` date string back to CE for saving. */
export const beToCeDate = (dateStr?: string): string => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts[0]) {
    const be = parseInt(parts[0], 10);
    if (!Number.isNaN(be)) {
      parts[0] = String(be - 543);
      return parts.join('-');
    }
  }
  return dateStr;
};

/**
 * Coerce a stored `yyyy-MM-dd` date to CE, whichever calendar it was written in.
 *
 * `broker_license_expiry_date` in production holds three populations, because more than one write
 * path has been feeding it: Gregorian years, raw Buddhist years, and Gregorian years that had the
 * Buddhist conversion applied once too often. A survey of 28,716 rows in September 2026 found
 * 21,539 Gregorian, 6,519 Buddhist, 648 over-converted and 10 unusable.
 *
 * The bands do not overlap for any date a person could plausibly hold, so the reading is
 * unambiguous: nobody has a licence expiring in the year 2570, nor one that expired in 1484.
 *
 * Returns '' for a year outside every band, so an unusable value reads as "not set" and can be
 * re-entered, rather than rendering a nonsense year in a date picker.
 */
export const resolveStoredDateToCe = (dateStr?: string): string => {
  if (!dateStr) return '';
  const year = parseInt(dateStr.split('-')[0] ?? '', 10);
  if (Number.isNaN(year)) return '';
  // Already CE.
  if (year >= 1900 && year <= 2199) return dateStr;
  // Buddhist era, written without conversion.
  if (year >= 2400 && year <= 2799) return beToCeDate(dateStr);
  // CE that was converted a second time; only trust it if it lands somewhere plausible.
  if (year < 1600 && year + 543 >= 1900 && year + 543 <= 2199) return ceToBeDate(dateStr);
  return '';
};

/**
 * Convert document to File object
 * @param doc - document
 * @returns File object */
export const toDocFile = (doc: {
  id: number;
  file: { original_file_name: string; mime_type: string };
  document_type: string;
}): File & { documentId?: number; documentType?: string } => {
  const f = new File([], doc.file.original_file_name, { type: doc.file.mime_type }) as File & {
    documentId?: number;
    documentType?: string;
  };
  f.documentId = doc.id;
  f.documentType = doc.document_type;
  return f;
};

/**
 * Whether a date of birth represents a valid age (at least 18 years old as of today).
 * Returns false for a missing date — callers that only validate a provided value should guard
 * on presence first.
 */
export const isDobValid = (dob: Date | null | undefined): boolean => {
  if (!dob) return false;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) age--;
  return age >= 18;
};

/**
 * Download a file from a URL, forcing a download instead of navigating to it.
 * Fetches as a blob so the anchor `download` attribute is honored even for
 * cross-origin URLs (e.g. S3). Throws if the fetch fails, so callers can decide
 * on a fallback (e.g. opening the URL in a new tab).
 * @param fileUrl - URL of the file to download
 * @param fileName - Name to save the downloaded file as
 */
export const downloadFileFromUrl = async (fileUrl: string, fileName: string): Promise<void> => {
  const response = await fetch(fileUrl);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // The browser reads the blob URL after `click()` returns — revoking it straight
  // away can cancel the save (PDFs in a multi-file download were dropped while
  // images went through, FTT-16785). Same delay as ops-dashboard `saveBlobAsFile`.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

/** Machine-readable reason a file failed validation; callers map it to a localized message. */
export type FileValidationError = 'size' | 'extension' | 'mimeType';

export interface FileValidationOptions {
  /** Permitted extensions, including the leading dot (e.g. ['.csv']). */
  allowedExtensions: string[];
  /** Optional permitted MIME types; skipped when omitted or when the browser reports no type. */
  allowedMimeTypes?: string[];
  /** Optional maximum size in bytes. */
  maxSize?: number;
}

/**
 * Validate an uploaded file against allowed extensions, MIME types, and max size.
 * Returns the first failing check as an error code, or null when the file is valid.
 * @param file - The file to validate
 * @param options - Allowed extensions/MIME types and optional max size
 */
export const validateUploadedFile = (
  file: File,
  { allowedExtensions, allowedMimeTypes, maxSize }: FileValidationOptions,
): FileValidationError | null => {
  if (maxSize && file.size > maxSize) return 'size';
  const ext = '.' + (file.name.split('.').pop()?.toLowerCase() ?? '');
  if (!allowedExtensions.includes(ext)) return 'extension';
  if (allowedMimeTypes && file.type && !allowedMimeTypes.includes(file.type)) return 'mimeType';
  return null;
};

/**
 * Options for a bank-name picker, with the stored value appended when it is not one of
 * the canonical names. Radix Select renders its label from a matching item, so without
 * this an unrecognised value shows as blank. The legacy portal's picker read its label
 * straight off the stored value, so this restores that behaviour. The appended entry is
 * disabled: it shows what is on the record, but can only be replaced by a canonical name.
 */
export const resolveBankOptions = (storedValue?: string): BankOption[] => {
  if (!storedValue || BANK_OPTIONS.some((option) => option.value === storedValue)) {
    return BANK_OPTIONS;
  }
  return [...BANK_OPTIONS, { value: storedValue, label: storedValue, disabled: true }];
};

const HTML_TAG = /<[^>]*>/g;

const HTML_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  '#39': "'",
  nbsp: ' ',
};

/** Remove every `<...>` tag, re-running until nothing changes. */
export const stripHtmlTags = (html: string): string => {
  let out = html;
  for (let prev = ''; prev !== out;) {
    prev = out;
    out = out.replace(HTML_TAG, '');
  }
  return out;
};

/** Decode the entities Django debug pages and Quill emit, in one pass so `&amp;lt;` stays `&lt;`. */
export const decodeHtmlEntities = (text: string): string =>
  text.replace(/&(amp|lt|gt|quot|#39|nbsp);/g, (_, name: string) => HTML_ENTITIES[name]);

/** Plain text out of an HTML fragment: tags removed, entities decoded, whitespace collapsed. */
export const htmlToText = (html: string): string => decodeHtmlEntities(stripHtmlTags(html)).replace(/\s+/g, ' ').trim();
